/**
 * `HTF` — the higher-timeframe projection indicator.
 *
 * Registers with `figures: []` and paints in `draw`, the `previousClose` pattern.
 * That is what keeps it OUT of the price axis: klinecharts' `calcRange` folds in
 * declared figure values only, so an indicator with none cannot stretch the scale.
 * The projected level is simply absent when it falls outside the visible range,
 * which is the point — a daily level would otherwise squash the intraday detail.
 *
 * The Style tab still offers colour and dash because the CATALOG declares a `line`
 * figure while this registration does not — the same split VPVR uses. The draw
 * reads `indicator.styles.lines[0]` positionally.
 */

import { registerIndicator, type Indicator, type KLineData } from 'klinecharts'
import { type IndicatorTemplateParam, registerOnce } from './registrationUtils'
import { PERIODS, type PeriodKey } from './resolution'
import {
  htfRuns,
  projectHtfBuckets,
  projectHtfField,
  type HtfBar,
  type HtfField,
} from './htfProjection'

const HTF_INDICATOR_NAME = 'HTF'

/** Default target — one hour reads well on a 1m–15m chart. */
const DEFAULT_TARGET: PeriodKey = '1H'
const DEFAULT_FIELD: HtfField = 'close'

/** How the coarser bar is drawn. */
export type HtfDisplay = 'candles' | 'line'
const DEFAULT_DISPLAY: HtfDisplay = 'candles'
const DEFAULT_WICKS = true
/** Draw the currently-building (incomplete) higher-timeframe bar by default. */
const DEFAULT_PLOT_INCOMPLETE = true

/** Fallback body colours by direction; overridden by the catalog's two figures. */
const DEFAULT_UP_COLOR = '#26A69A'
const DEFAULT_DOWN_COLOR = '#EF5350'

/**
 * Body, wick and outline opacity.
 *
 * The structure is carried by the OUTLINE, not the fill. Tinting heavily enough to
 * read the bar washes out the intraday candles it is meant to give context to, so
 * the fills stay deliberately faint and a crisp 1px border does the work.
 */
const BODY_ALPHA = 0.2
const WICK_ALPHA = 0.066
/**
 * Solid outline around the BODY only.
 *
 * Not around the full high-low range: the wick region is context, and boxing it
 * draws the eye to the extremes rather than to the open-to-close move. The body
 * border is also what separates one coarser bar from the next — with fills alone,
 * adjacent same-direction buckets merge into a single continuous block.
 */
const BODY_BORDER_ALPHA = 0.54
/**
 * Floor for the body height, so a doji stays visible instead of collapsing to a
 * zero-height rect.
 *
 * There is deliberately NO separate close line. The close is always one edge of
 * the body — the top of an up bar, the bottom of a down bar — so a hairline there
 * sat exactly on the border and doubled its thickness on that side: a heavy top
 * edge on green bars, a heavy bottom edge on red ones. The border already marks
 * both the open and the close, so the extra line only broke the symmetry.
 */
const MIN_BODY_PX = 1.5

/** Fallback stroke when no style override is set. */
const DEFAULT_LINE_COLOR = '#A78BFA'

/** Dash pattern for the projected step. Solid would compete with the price line. */
const LINE_DASH = [5, 4]
/** The forming bucket is provisional, so it reads lighter. */
const FORMING_ALPHA = 0.45

type HtfCtx = {
  fillStyle: string | CanvasGradient | CanvasPattern
  strokeStyle: string | CanvasGradient | CanvasPattern
  lineWidth: number
  globalAlpha: number
  fillRect: (x: number, y: number, w: number, h: number) => void
  strokeRect: (x: number, y: number, w: number, h: number) => void
  save: () => void
  restore: () => void
  beginPath: () => void
  moveTo: (x: number, y: number) => void
  lineTo: (x: number, y: number) => void
  stroke: () => void
  setLineDash: (segments: number[]) => void
}

export interface HtfDrawParams {
  ctx: HtfCtx
  kLineDataList: KLineData[]
  visibleRange: { from: number; to: number }
  yAxis: { convertToPixel: (value: number) => number }
  xAxis: { convertToPixel: (dataIndex: number) => number }
  barSpace: { halfBar: number }
  indicator: {
    calcParams?: unknown[]
    extendData?: unknown
    styles?: {
      lines?: Array<{
        color?: string
        size?: number
        style?: string
        dashedValue?: number[]
        visible?: boolean
      }>
    } | null
  }
}

/** Resolve the chart's own bar interval, which the draw cannot otherwise see. */
function chartMsOf(extendData: unknown): number {
  const ext = extendData as { periodKey?: string; periodMs?: number } | undefined
  if (ext?.periodMs && Number.isFinite(ext.periodMs)) return ext.periodMs
  const key = ext?.periodKey as PeriodKey | undefined
  return key && PERIODS[key] ? PERIODS[key].ms : 0
}

/** Pixel span of a bucket run, edge to edge rather than centre to centre so
 *  consecutive coarser bars abut instead of leaving a gap. */
function runSpan(
  xAxis: { convertToPixel: (i: number) => number },
  halfBar: number,
  startIdx: number,
  endIdx: number,
): { x: number; w: number } | null {
  const x1 = xAxis.convertToPixel(startIdx) - halfBar
  const x2 = xAxis.convertToPixel(endIdx) + halfBar
  if (!Number.isFinite(x1) || !Number.isFinite(x2)) return null
  return { x: x1, w: Math.max(x2 - x1, 0) }
}

/**
 * Draw the projection: one shaded bar per coarser-timeframe bucket, or a stepped
 * line of a single field.
 *
 * In `candles` mode each bucket is a faint wick-range rectangle (low..high), a
 * slightly stronger body rectangle (open..close), and a solid 1px border around
 * the body — coloured by direction. See the paint-order note inside: the emit
 * order is inverted because `zLevel: -1` composites destination-over.
 */
export function drawHtf(params: HtfDrawParams): boolean {
  const { ctx, kLineDataList, visibleRange, yAxis, xAxis, barSpace, indicator } = params
  const cp = indicator.calcParams ?? []
  const targetKey = String(cp[0] ?? DEFAULT_TARGET) as PeriodKey
  const target = PERIODS[targetKey]
  if (!target) return false
  // Positional, matching the catalog: [0] target, [1] display, [2] field, [3] wicks.
  const display: HtfDisplay = String(cp[1] ?? DEFAULT_DISPLAY) === 'line' ? 'line' : 'candles'
  const field = (String(cp[2] ?? DEFAULT_FIELD) as HtfField) ?? DEFAULT_FIELD
  // Wick visibility moved onto the wick pair's up figure (styles.lines[4]) so
  // the Style tab shows Body/Border/Wicks together; the old cp[3] slot is
  // retained in persisted arrays but no longer read.
  const wickUp = indicator.styles?.lines?.[4]
  const wickDown = indicator.styles?.lines?.[5]
  const showWicks = wickUp?.visible !== false
  // [4] plotIncomplete: draw the currently-building bucket, or hide it. Default
  // TRUE (only an explicit `false` hides it), so old 4-element persisted arrays
  // and fresh instances keep today's behaviour of drawing it dimmed.
  const plotIncomplete = cp[4] !== false

  const chartMs = chartMsOf(indicator.extendData)
  if (chartMs <= 0) return false

  const from = Math.max(0, visibleRange.from)
  const to = Math.min(kLineDataList.length, visibleRange.to)
  if (to <= from) return false

  const bars: HtfBar[] = kLineDataList.map((d) => ({
    timestamp: d.timestamp,
    open: d.open,
    high: d.high,
    low: d.low,
    close: d.close,
  }))

  // Projected over the WHOLE series, not the viewport: a bucket straddling the
  // left edge must carry its full bucket's values, not the visible part's.
  if (display === 'line') {
    const points = projectHtfField(bars, target.ms, field, chartMs)
    const runs = htfRuns(points, from, to)
    if (runs.length === 0) return false
    const lineStyle = indicator.styles?.lines?.[0]
    const color = lineStyle?.color ?? DEFAULT_LINE_COLOR

    ctx.save()
    // Solid honours the Style tab; anything else keeps the signature HTF dash.
    ctx.setLineDash(lineStyle?.style === 'solid' ? [] : (lineStyle?.dashedValue ?? LINE_DASH))
    ctx.strokeStyle = color
    ctx.lineWidth = lineStyle?.size ?? 1
    for (const run of runs) {
      // Hide the currently-building bucket entirely when the user opts out.
      if (!plotIncomplete && run.point.forming) continue
      const y = yAxis.convertToPixel(run.point.value)
      if (!Number.isFinite(y)) continue
      const span = runSpan(xAxis, barSpace.halfBar, run.startIdx, run.endIdx)
      if (!span) continue
      ctx.globalAlpha = run.point.forming ? FORMING_ALPHA : 1
      ctx.beginPath()
      ctx.moveTo(span.x, y)
      ctx.lineTo(span.x + span.w, y)
      ctx.stroke()
    }
    ctx.setLineDash([])
    ctx.restore()
    return false
  }

  const buckets = projectHtfBuckets(bars, target.ms, chartMs)
  const runs = htfRuns(buckets, from, to)
  if (runs.length === 0) return false

  const upColor = indicator.styles?.lines?.[0]?.color ?? DEFAULT_UP_COLOR
  const downColor = indicator.styles?.lines?.[1]?.color ?? DEFAULT_DOWN_COLOR
  /**
   * The body border is an up/down pair (figures [2]/[3]): hideable as a pair,
   * per-direction colours, width shared with the body's Line width. The
   * empty-string sentinel (and absent styles) falls back to the body's own
   * tint — the original border look.
   */
  const borderUp = indicator.styles?.lines?.[2]
  const borderDown = indicator.styles?.lines?.[3]
  const borderVisible = borderUp?.visible !== false
  // The body itself is hideable too (the Style tab's Body eye) — border and
  // wicks still draw, outlining the bucket without the fill.
  const bodyVisible = indicator.styles?.lines?.[0]?.visible !== false
  const strokeWidth = indicator.styles?.lines?.[0]?.size ?? 1

  ctx.save()
  // Explicitly solid. The context may arrive with a dash pattern left on it — line
  // mode sets one — and an inherited dash turned the body border into a dashed
  // outline. Never assume the incoming line style.
  ctx.setLineDash([])
  for (const run of runs) {
    const b = run.point
    // Hide the currently-building bucket entirely when the user opts out.
    if (!plotIncomplete && b.forming) continue
    const span = runSpan(xAxis, barSpace.halfBar, run.startIdx, run.endIdx)
    if (!span || span.w <= 0) continue

    const yOpen = yAxis.convertToPixel(b.open)
    const yClose = yAxis.convertToPixel(b.close)
    const yHigh = yAxis.convertToPixel(b.high)
    const yLow = yAxis.convertToPixel(b.low)
    if (![yOpen, yClose, yHigh, yLow].every(Number.isFinite)) continue

    const rising = b.close >= b.open
    const color = rising ? upColor : downColor
    // A provisional bucket reads lighter, as in line mode.
    const dim = b.forming ? FORMING_ALPHA : 1
    ctx.fillStyle = color
    ctx.strokeStyle = (rising ? borderUp?.color : borderDown?.color) || color
    ctx.lineWidth = strokeWidth

    const rangeTop = Math.min(yHigh, yLow)
    const rangeH = Math.abs(yLow - yHigh)
    const bodyTop = Math.min(yOpen, yClose)
    // A doji would be zero-height and invisible, so it keeps a hairline.
    const bodyH = Math.max(Math.abs(yClose - yOpen), MIN_BODY_PX)

    /**
     * Painted FRONT TO BACK, which reads inverted on purpose.
     *
     * `zLevel: -1` makes klinecharts composite this indicator with
     * `destination-over`, so each successive draw lands BEHIND the previous one.
     * Emitting in the natural back-to-front order would bury the border under the
     * fills it delimits and tint the body twice where it overlaps the wick.
     *
     * So: border, then body fill, then wick fill last — putting the wick shading
     * furthest back and the border in front, all of it still beneath the bars.
     */
    if (borderVisible) {
      ctx.globalAlpha = BODY_BORDER_ALPHA * dim
      ctx.strokeRect(span.x, bodyTop, span.w, bodyH)
    }

    if (bodyVisible) {
      ctx.globalAlpha = BODY_ALPHA * dim
      ctx.fillRect(span.x, bodyTop, span.w, bodyH)
    }

    if (showWicks) {
      // Own per-direction colour when styled; the empty-string catalog
      // sentinel (and absent styles) falls back to the body's up/down tint.
      ctx.fillStyle = (rising ? wickUp?.color : wickDown?.color) || color
      ctx.globalAlpha = WICK_ALPHA * dim
      ctx.fillRect(span.x, rangeTop, span.w, rangeH)
    }
  }
  ctx.restore()

  // false, not true: this paints an annotation and must not suppress whatever
  // else klinecharts renders on the pane.
  return false
}

/** Register the `HTF` projection indicator with klinecharts (once). */
export const registerHtfIndicator = registerOnce(() => {
  registerIndicator({
    name: HTF_INDICATOR_NAME,
    shortName: 'HTF',
    series: 'price' as never,
    calcParams: [
      DEFAULT_TARGET,
      DEFAULT_DISPLAY,
      DEFAULT_FIELD,
      DEFAULT_WICKS,
      DEFAULT_PLOT_INCOMPLETE,
    ],
    /**
     * BEHIND the candles. klinecharts paints a negative zLevel with
     * `destination-over`, which is the only way a shaded region can sit under the
     * price bars — every other overlay here (VPVR, VOL_OVERLAY) does the same.
     * Without it the projection painted OVER the candles and washed them out,
     * which is exactly what made the chart hard to read.
     */
    zLevel: -1,
    // No figures — see the module note. This is the ONLY thing keeping the
    // projection out of the price-axis range.
    figures: [],
    calc: (dataList: KLineData[]) => dataList.map(() => ({})),
    draw: ((p: HtfDrawParams & { indicator: Indicator }) => drawHtf(p)) as never,
    // The React legend row carries the label; a canvas tooltip would duplicate it.
    createTooltipDataSource: () => ({ name: '', calcParamsText: '', icons: [], values: [] }),
  } as IndicatorTemplateParam)
})

export { HTF_INDICATOR_NAME }
